import React from 'react'
import { Teacher, useAppContext } from '../../providers/context'
import { ThreePanelLayout } from '../../layouts/threePanel'
import { CharcoalInput, DelayedTextInput, TextInput } from '../../components/form/textInput'
import { ListButton, ListButtonCheckbox } from '../../components/simple/listButton'
import { makeRequest, useCachedRequest, usePaginatedData, useRequest } from '../../request'
import { ContactsData } from '../home/contacts'
import { Divider } from '../../components/simple/divider'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { IconFileDownload, IconFileUpload, IconPlus, IconSearch } from '../../icons/library'
import { ControlledModal } from '../../layouts/components/modal'
import { FloatingLoader, Loader } from '../../components/simple/loader'
import { ContactSibling } from './contactComponents'
import { StudentContact } from './familyContactData'
import { ContactGuardian } from './guardianContact'
import { ContactStudent } from './studentContact'
import { FileUploadRow, UploadedFile } from '../../components/media/fileUpload'
import { Select, SimpleSelect } from '../../components/form/select'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { Toasts } from '../../providers/toast'
import { useLanguages } from '../../providers/languages'
import { Popups } from '../../providers/popups'

export const FamilyContactsPage: React.FC<{
  user: Teacher
}> = ({ user }) => {
  const [search, setSearch] = React.useState('')

  const [filteredSchools, setFilteredSchools] = React.useState(new Set<number>())
  const [filteredGroups, setFilteredGroups] = React.useState(new Set<number>())
  const [onlyFailedContacts, setOnlyFailedContacts] = React.useState(false)
  const [onlyDeletedUsers, setOnlyDeletedUsers] = React.useState(false)

  const [showNewIndividualModal, setShowNewIndividualModal] = React.useState(false)
  const [showCSVImportModal, setShowCSVImportModal] = React.useState(false)

  const { data: userContacts } = useRequest<ContactsData>('/frontend_api/getContacts')

  const contactPostData = React.useMemo(
    () => ({
      search,
      schools: Array.from(filteredSchools),
      groups: Array.from(filteredGroups),
      onlyFailedContacts,
      onlyDeletedUsers,
    }),
    [search, filteredSchools, filteredGroups, onlyFailedContacts, onlyDeletedUsers]
  )
  const {
    values: contacts,
    loadingMore,
    loading,
    loadMore,
    reload: reloadContacts,
  } = usePaginatedData<StudentContact, typeof contactPostData>('/frontend_api/getFamilyContacts', contactPostData)

  return (
    <ThreePanelLayout
      noMobile
      user={user}
      left={
        <>
          <DelayedTextInput
            className={`rounded-full outline outline-1 outline-charcoal-text ${CharcoalInput}`}
            value={search}
            onChange={setSearch}
            placeholder="Search"
            icon={<IconSearch />}
          />
          {(user.schools.length > 1 || user.districtAccess) && (
            <div className="flex flex-col gap-1">
              <div className="ml-2 mt-2 font-bold">Filter Schools</div>
              {(user.districtAccess ? user.district.schools : user.schools).sort().map((school) => (
                <ListButton
                  key={school.id}
                  onClick={() => {
                    const schools = new Set(filteredSchools)
                    if (!schools.has(school.id)) schools.add(school.id)
                    else schools.delete(school.id)
                    setFilteredSchools(schools)
                  }}
                >
                  {school.name}
                  <ListButtonCheckbox checked={filteredSchools.has(school.id)} />
                </ListButton>
              ))}
            </div>
          )}
          {userContacts?.groups && userContacts.groups.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="ml-2 mt-2 font-bold">Filter Classes And Groups</div>
              {userContacts.groups.map((group) => (
                <ListButton
                  key={group.id}
                  onClick={() => {
                    const groups = new Set(filteredGroups)
                    if (!groups.has(group.id)) groups.add(group.id)
                    else groups.delete(group.id)
                    setFilteredGroups(groups)
                  }}
                >
                  {group.name}
                  <ListButtonCheckbox checked={filteredGroups.has(group.id)} />
                </ListButton>
              ))}
            </div>
          )}
          {(user.schools.length > 1 ||
            user.districtAccess ||
            (userContacts?.groups && userContacts.groups.length > 0)) && <Divider className="!border-charcoal-text" />}
          <ListButton onClick={() => setOnlyFailedContacts(!onlyFailedContacts)}>
            Only Failed Contacts
            <ListButtonCheckbox checked={onlyFailedContacts} />
          </ListButton>
          <ListButton onClick={() => setOnlyDeletedUsers(!onlyDeletedUsers)}>
            Only Deleted Users
            <ListButtonCheckbox checked={onlyDeletedUsers} />
          </ListButton>
        </>
      }
      bottomLeft={
        <>
          {user.allowedToAddFamilies && (
            <Button
              tooltip="Add an individual family"
              logo
              shape={ButtonShape.iconCircleLarge}
              onClick={() => setShowNewIndividualModal(true)}
            >
              <IconPlus size="lg" />
            </Button>
          )}
          {user.admin === 2 && (user.districtAccess || user.district.allow_all_to_add_users) && (
            <>
              <Button
                buttonStyle={ButtonStyle.dark}
                shape={ButtonShape.iconCircleLarge}
                onClick={() => setShowCSVImportModal(true)}
                tooltip="Import students from CSV"
              >
                <IconFileUpload size="lg" />
              </Button>
              <Button
                buttonStyle={ButtonStyle.dark}
                shape={ButtonShape.iconCircleLarge}
                link="/csvExportStudents"
                tooltip="Export students as CSV"
              >
                <IconFileDownload size="lg" />
              </Button>
            </>
          )}
        </>
      }
      floating={
        <>
          {showNewIndividualModal && (
            <NewIndividualModal
              user={user}
              onClose={() => {
                setShowNewIndividualModal(false)
                reloadContacts()
              }}
            />
          )}
          {showCSVImportModal && <CSVImportModal user={user} onClose={() => setShowCSVImportModal(false)} />}
        </>
      }
    >
      {!contacts || (contacts.length === 0 && loading) ? (
        <Loader />
      ) : (
        <div
          className={`flex flex-col items-start gap-3 overflow-auto p-3 ${loading ? 'pointer-events-none opacity-50' : ''}`}
        >
          {contacts.map((contact) => (
            <ContactRow key={contact.id} contact={contact} reloadContacts={reloadContacts} user={user} />
          ))}
          {loadMore && !loading && (
            <Button className="mx-auto my-3" logo small onClick={loadMore} disabled={loadingMore}>
              Load More
            </Button>
          )}
        </div>
      )}
    </ThreePanelLayout>
  )
}

const ContactRow: React.FC<{
  contact: StudentContact
  reloadContacts: () => void
  user: Teacher
}> = ({ contact, reloadContacts, user }) => {
  return (
    <div className="flex gap-3 rounded-[12.5px] bg-lightBlue p-3 shadow-even-inset">
      <ContactStudent student={contact} reloadContacts={reloadContacts} user={user} />
      {contact.guardians.length + contact.siblings.length > 0 && (
        <div className="flex flex-col gap-2">
          {contact.guardians.length > 0 && (
            <>
              <div className="font-semibold text-charcoal-text">Guardians</div>
              <div className="-m-1 flex gap-3 p-1">
                {contact.guardians.map((g) => (
                  <ContactGuardian
                    key={g.id}
                    guardian={g}
                    user={user}
                    reloadContacts={reloadContacts}
                    student={contact}
                  />
                ))}
              </div>
            </>
          )}
          {contact.siblings.length > 0 && (
            <>
              <div className="font-semibold text-charcoal-text">Siblings</div>
              <div className="-m-1 flex gap-3 p-1">
                {contact.siblings.map((s) => (
                  <ContactSibling key={s.id} sibling={s} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

const NewIndividualModal: React.FC<{ user: Teacher; onClose: () => void }> = ({ user, onClose }) => {
  const languages = useLanguages()

  const [studentFirstName, setStudentFirstName] = React.useState('')
  const [studentLastName, setStudentLastName] = React.useState('')
  const [studentEmail, setStudentEmail] = React.useState('')
  const [studentPhone, setStudentPhone] = React.useState('')
  const languageOptions = React.useMemo(() => languages.getOptions((l) => l.isStandard), [languages])
  const [studentLanugage, setStudentLanugage] = React.useState(languageOptions.find((l) => l.value === 'en')!)

  const { data: gradeOptions } = useCachedRequest<string[]>('/frontend_api/getAllDistrictGrades', user.district.id)
  const [studentGrade, setStudentGrade] = React.useState('')
  React.useEffect(() => gradeOptions && setStudentGrade(gradeOptions[0]), [gradeOptions])

  const [studentGradeOther, setStudentGradeOther] = React.useState('')

  const schools = React.useMemo(
    () =>
      (user.districtAccess ? user.district.schools : user.schools).map((s) => ({
        name: s.name,
        value: s.id,
      })),
    [user]
  )
  const [studentSchool, setStudentSchool] = React.useState(schools[0])

  const [addStudentToYourClass, setAddStudentToYourClass] = React.useState(false)

  const [guardian1Enabled, setGuardian1Enabled] = React.useState(false)
  const [guardian1FirstName, setGuardian1FirstName] = React.useState('')
  const [guardian1LastName, setGuardian1LastName] = React.useState('')
  const [guardian1Email, setGuardian1Email] = React.useState('')
  const [guardian1Phone, setGuardian1Phone] = React.useState('')
  const [guardian1Lanugage, setGuardian1Lanugage] = React.useState(languageOptions.find((l) => l.value === 'en')!)

  const [guardian2Enabled, setGuardian2Enabled] = React.useState(false)
  const [guardian2FirstName, setGuardian2FirstName] = React.useState('')
  const [guardian2LastName, setGuardian2LastName] = React.useState('')
  const [guardian2Email, setGuardian2Email] = React.useState('')
  const [guardian2Phone, setGuardian2Phone] = React.useState('')
  const [guardian2Lanugage, setGuardian2Lanugage] = React.useState(languageOptions.find((l) => l.value === 'en')!)

  const [loading, setLoading] = React.useState(false)

  return (
    <ControlledModal
      open={true}
      onOpenChange={(c) => {
        if (!c) onClose()
      }}
      title="Add A Student Directly"
      disabled={loading}
      showLoader={loading}
      size="sm"
    >
      <TextInput placeholder="First Name" value={studentFirstName} onChange={setStudentFirstName} blue />
      <TextInput placeholder="Last Name" value={studentLastName} onChange={setStudentLastName} blue />
      <TextInput placeholder="Email" value={studentEmail} onChange={setStudentEmail} blue validation="email" />
      <TextInput placeholder="Phone Number" value={studentPhone} onChange={setStudentPhone} blue validation="phone" />
      <Select options={languageOptions} value={studentLanugage} onChange={setStudentLanugage} blue />
      {gradeOptions && (
        <>
          <SimpleSelect options={[...gradeOptions, 'Other']} value={studentGrade} onChange={setStudentGrade} blue />
          {studentGrade === 'Other' && (
            <TextInput placeholder="Grade" value={studentGradeOther} onChange={setStudentGradeOther} blue />
          )}
        </>
      )}
      {schools.length > 1 && <Select options={schools} value={studentSchool} onChange={setStudentSchool} blue />}
      {user.district.allowAllToAddUsers && (
        <LabeledCheckbox
          checked={addStudentToYourClass}
          onChecked={setAddStudentToYourClass}
          label="Add to your class"
          labelBefore
        />
      )}
      <LabeledCheckbox checked={guardian1Enabled} onChecked={setGuardian1Enabled} label="Guardian 1" labelBefore />
      {guardian1Enabled && (
        <>
          <TextInput placeholder="First Name" value={guardian1FirstName} onChange={setGuardian1FirstName} blue />
          <TextInput placeholder="Last Name" value={guardian1LastName} onChange={setGuardian1LastName} blue />
          <TextInput placeholder="Email" value={guardian1Email} onChange={setGuardian1Email} blue validation="email" />
          <TextInput
            placeholder="Phone Number"
            value={guardian1Phone}
            onChange={setGuardian1Phone}
            blue
            validation="phone"
          />
          <Select options={languageOptions} value={guardian1Lanugage} onChange={setGuardian1Lanugage} blue />
        </>
      )}
      <LabeledCheckbox checked={guardian2Enabled} onChecked={setGuardian2Enabled} label="Guardian 2" labelBefore />
      {guardian2Enabled && (
        <>
          <TextInput placeholder="First Name" value={guardian2FirstName} onChange={setGuardian2FirstName} blue />
          <TextInput placeholder="Last Name" value={guardian2LastName} onChange={setGuardian2LastName} blue />
          <TextInput placeholder="Email" value={guardian2Email} onChange={setGuardian2Email} blue validation="email" />
          <TextInput
            placeholder="Phone Number"
            value={guardian2Phone}
            onChange={setGuardian2Phone}
            blue
            validation="phone"
          />
          <Select options={languageOptions} value={guardian2Lanugage} onChange={setGuardian2Lanugage} blue />
        </>
      )}
      <Button
        className="mt-3 self-start"
        logo
        onClick={() => {
          setLoading(true)
          makeRequest('/csvImportRow', {
            StudentFirstName: studentFirstName,
            StudentLastName: studentLastName,
            StudentEmail: studentEmail,
            StudentPhoneNumber: studentPhone,
            StudentGrade: studentGrade === 'Other' ? studentGradeOther : studentGrade,
            StudentLanguage: studentLanugage.value,
            SchoolID: studentSchool.value,
            Guardian1FirstName: guardian1FirstName,
            Guardian1LastName: guardian1LastName,
            Guardian1Email: guardian1Email,
            Guardian1PhoneNumber: guardian1Phone,
            Guardian1Language: guardian1Lanugage.value,
            Guardian2FirstName: guardian2FirstName,
            Guardian2LastName: guardian2LastName,
            Guardian2Email: guardian2Email,
            Guardian2PhoneNumber: guardian2Phone,
            Guardian2Language: guardian2Lanugage.value,
            add_to_class: addStudentToYourClass,
          })
            .then(onClose)
            .catch((e) => {
              console.log(e)
              Toasts.showToast({ message: 'Failed to import this data.' })
            })
        }}
      >
        Import
      </Button>
    </ControlledModal>
  )
}

const CSVImportModal: React.FC<{ user: Teacher; onClose: () => void }> = ({ user, onClose }) => {
  const { state } = useAppContext()
  const [loading, setLoading] = React.useState(false)
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([])
  return (
    <ControlledModal
      open={true}
      onOpenChange={(c) => {
        if (!c) onClose()
      }}
      title="Import Students From CSV"
      size="sm"
      disabled={loading}
      showLoader={loading}
    >
      <b className="text-lg">Import students from Google Spreadsheet</b>
      <div>
        You can open a spreadsheet of your students by clicking these links. You can choose to{' '}
        <a
          className="text-link"
          onClick={(e) => {
            e.preventDefault()
            Popups.confirm({
              message: "Are you sure you want to replace your spreadsheet's data with the user data on ReachMyTeach?",
              yesText: 'Yes',
              noText: 'No',
            }).then((confirmed) => {
              if (confirmed) {
                window.open('/spreadsheet/update?type=students', '_blank')
              }
            })
          }}
          href="/spreadsheet/update?type=students"
        >
          click here to update spreadsheet
        </a>{' '}
        with the current info in the system, or{' '}
        <Button
          buttonStyle={ButtonStyle.link}
          shape={ButtonShape.inlineSmall}
          openInNewTab
          link="/spreadsheet/open?type=students"
        >
          open spreadsheet
        </Button>
      </div>
      <b>To import those changes, you first will need to validate them:</b>

      <form method="post" action="/csvImport" className="flex flex-col gap-2">
        <input type="hidden" name="_token" value={state.csrf} />
        <input type="hidden" name="spreadsheet" value="students" />
        <div className="flex items-center gap-2">
          <input type="checkbox" name="deactivate" />
          <label>Deactivate students not in this file</label>
        </div>
        <button
          className={`${ButtonStyle.logo} ${ButtonShape.standard} self-start`}
          name="action"
          value="Test"
          type="submit"
          onClick={() => setLoading(true)}
        >
          Validate
        </button>
      </form>
      <Divider>OR</Divider>
      <b className="text-lg">Import directly</b>
      <FileUploadRow maxFiles={1} uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles}>
        <Button>Upload File</Button>
      </FileUploadRow>
      <form method="post" action="/csvImport" className="flex flex-col gap-2">
        <input type="hidden" name="_token" value={state.csrf} />
        <input
          type="hidden"
          name="filenamestudents"
          value={uploadedFiles.length > 0 ? uploadedFiles[0].fileName : ''}
        />
        <div className="flex items-center gap-2">
          <input type="checkbox" name="deactivate" />
          <label>Deactivate students not in this file</label>
        </div>
        <button
          className={`${ButtonStyle.logo} ${ButtonShape.standard} self-start ${uploadedFiles.length === 0 ? 'opacity-50' : ''}`}
          name="action"
          value="Test"
          type="submit"
          onClick={() => setLoading(true)}
          disabled={uploadedFiles.length === 0}
        >
          Validate
        </button>
      </form>
      <a href="/csvExportStudents?justCSV=1" target="_blank" className="text-link">
        Download current students
      </a>
    </ControlledModal>
  )
}
